import Modal from '@components/v2/ui/Modal/Modal';
import {} from '@mui/material';
import { Box, Button, Grid } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import AboutDescription from '../../components/Site/AboutDescription';
import AboutSection from '../../components/Site/AboutSection';
import BannerSection from '../../components/Site/BannerSection';
import InstagramFeedSection from '../../components/Site/InstagramFeedSection';
import PersonSignatureSection from '../../components/Site/PersonSignatureSection';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { usePageTitleManager } from '../../hooks/usePageTitleManager';
import LangEnum from '../../lang/LangEnum';
import PATHS from '../../routes/routes.const';
import useSharedEntitystore from '../../stores/SharedEntities/sharedEntities.store';
import BrandAboutSectionForm from '../brand/BrandAboutSectionForm';
import { useChatContext } from '../chat/contexts/chat/chat.context';
import { useApplicationContext } from '../context/ApplicationContext/ApplicationContext';
import { useAuthContext } from '../login/AuthContext';
import ConnectionActionButton from '../my-network/connections/ConnectionActionButton';
import { Explore } from '../my-network/explore/myNetworkExplore.types';
import { validateLink } from '../shared-entities/repository/shared-entities.repository';
import {
  getSharedEntityInternalPath,
  getSharedPublicId,
} from '../shared-entities/utils/sharedEntities.utils';
import EditButton from '../showroom-storefront/EditButton';
import ShowroomStorefrontLoading from '../showroom-storefront/ShowroomStorefrontLoading';
import { useShowroomStorefrontStyles } from '../showroom-storefront/showroomStoreFront.styles';
import { useTranslation } from '../translation/TranslationContext';
import BrandEmphasisCatalog from './BrandEmphasisCatalog';
import BrandEmphasisProductsSection from './BrandEmphasisProductsSection';
import BrandPdfMidiasSection from './BrandPdfMidiasSection';
import PublicBrandShowroomHeader from './PublicBrandShowroomHeader';
import useBrandShowroom from './useBrandShowroom';

type BrandShowroomProps = {
  brandId: string;
};

const BrandShowroom = ({ brandId }: BrandShowroomProps) => {
  const user = localStorage.getItem('publicUser');
  const isInstagramAvailable = import.meta.env.VITE_FEATURE_IS_AVAILABLE === 'true';
  const { isEnableChat } = useFeatureFlag();
  const { openPrivateChatBetweenStoreAndBrandOrChatCreation } = useChatContext();

  const {
    aboutData,
    signaturesData,
    brandShowroom,
    exploreConnection,
    loading,
    tag,
    editingAboutSection,
    canEditShowroom,
    canShowEditButton,
    viewMode,
    toggleViewMode,
    load: loadShoroom,
    refreshExploreConnection,
    onSaveContact,
    closeEditingSection,
    afterEditShowroom,
    openEditingSection,
  } = useBrandShowroom(brandId);

  const { loggedin } = useAuthContext();
  const isPublicView = !loggedin && !user;
  const isSharedLinkView = !loggedin && !isPublicView;
  usePageTitleManager({
    overridePageTitleKey: LangEnum.MAIN_DRAWER_SHOWROOM,
  });

  const { team, isStore, state, changeBrand } = useApplicationContext();
  const { previousUrl, setPreviousUrl } = useSharedEntitystore(
    useShallow((sharedEntityState) => ({
      previousUrl: sharedEntityState.previousUrl,
      setPreviousUrl: sharedEntityState.setPreviousUrl,
    })),
  );
  const loadingOrBrandMissing = loading || !brandShowroom;
  const history = useHistory();

  const { classes } = useShowroomStorefrontStyles({
    hasTopSpacing: isPublicView,
    hasWorkspaceGutter: loggedin,
  });
  const { translate } = useTranslation();

  const enableLink = !!exploreConnection?.connection || team?.brand?.id === brandId;

  const readOnly = !canEditShowroom;

  const changeContext = useCallback(() => {
    const newBrand = state.brands.find((brand) => brand.id === brandId);

    if (exploreConnection?.connection && newBrand && brandId !== state.brand?.id) {
      changeBrand(newBrand);
    }
  }, [changeBrand, exploreConnection?.connection, brandId, state.brand?.id, state.brands]);

  const validatePublicLink = async () => {
    if (!previousUrl) {
      return;
    }

    const publicId = getSharedPublicId(previousUrl);

    if (!publicId) {
      return;
    }

    const response = await validateLink(publicId);

    if (response.ok && response.data && state?.brand?.id === response.data.brand.id) {
      history.push(getSharedEntityInternalPath(response.data.entity, response.data.entityId));
      setPreviousUrl(undefined);
    }
  };

  useEffect(() => {
    validatePublicLink();
  }, [state.brand]);

  useEffect(() => {
    loadShoroom(brandId);
  }, [brandId, loadShoroom]);

  useEffect(() => {
    if (isStore) {
      changeContext();
    }
  }, [changeContext, isStore]);

  if (tag === 'error') {
    return <Redirect to={PATHS.public.notFound.path} />;
  }

  if (loadingOrBrandMissing) {
    return <ShowroomStorefrontLoading />;
  }

  const showConnectionButton = isStore && exploreConnection && !exploreConnection.connection;
  const showSendMessageButton =
    isEnableChat && isStore && exploreConnection && exploreConnection.connection;

  return (
    <>
      {isPublicView && (
        <PublicBrandShowroomHeader
          data={aboutData}
          slug={brandShowroom?.slug}
          logos={brandShowroom?.logos}
        />
      )}
      {!readOnly && (
        <Modal
          isOpen={editingAboutSection}
          size="expanded"
          header={{ title: translate(LangEnum.SHOWROOM_EDIT_MAIN_SECTION) }}
          isDismissable={false}
          onClose={() => closeEditingSection()}
        >
          <BrandAboutSectionForm
            initialData={brandShowroom}
            onSave={afterEditShowroom}
            onCancel={closeEditingSection}
          />
        </Modal>
      )}
      <div className={classes.root}>
        <Box className={classes.bannerFullBleed}>
          <BannerSection
            readOnly={readOnly}
            onEdit={openEditingSection}
            bannerUrl={brandShowroom?.bannerUrl}
            placeHolder="/images/showroom-banner.svg"
            objectFit={brandShowroom?.bannerUrl ? 'cover' : 'contain'}
          />
        </Box>

        <AboutSection
          loading={false}
          sectionProps={{
            bgColor: 'white',
            readOnly,
            editLabel: LangEnum.SHOWROOM_EDIT_BRAND_DATA,
            onEdit: openEditingSection,
          }}
          aboutInfo={aboutData}
        >
          {aboutData?.about && (
            <AboutDescription
              title={translate(LangEnum.SHOWROOM_ABOUT)}
              description={aboutData.about}
            />
          )}
        </AboutSection>

        <BrandEmphasisProductsSection
          sectionProps={{ readOnly }}
          brandId={brandId}
          viewMode={viewMode}
        />

        <BrandEmphasisCatalog
          brandId={brandId}
          sectionProps={{
            readOnly,
            editLabel: LangEnum.SHOWROOM_EDIT_CATALOGS,
          }}
          enableLink={enableLink}
        />

        <BrandPdfMidiasSection sectionProps={{ readOnly }} brandId={brandId} viewMode={viewMode} />

        <PersonSignatureSection
          sectionProps={{ readOnly, bgColor: 'white' }}
          signatures={signaturesData}
          title={translate(LangEnum.SHOWROOM_CONTACTS)}
          onSubmit={onSaveContact}
        />

        {isInstagramAvailable && (
          <InstagramFeedSection
            ownerId={brandId}
            username={brandShowroom.instagramUrl}
            ownerType="brand"
            sectionProps={{ bgColor: 'white', readOnly }}
          />
        )}

        <Box className={classes.floatActions}>
          <Grid
            container
            spacing={1}
            sx={{
              justifyContent: 'flex-end',
            }}
          >
            <Grid>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                {showSendMessageButton && (
                  <Button
                    onClick={() =>
                      openPrivateChatBetweenStoreAndBrandOrChatCreation(
                        team?.store?.id!,
                        brandShowroom.id,
                      )
                    }
                    className={classes.sendMessage}
                    variant="outlined"
                    color="primary"
                  >
                    {translate(LangEnum.CHAT_SHOWROOM_STOREFRONT_SEND_MESSAGE_BUTTON)}
                  </Button>
                )}

                {showConnectionButton && (
                  <Box className={classes.connectionButton}>
                    <ConnectionActionButton
                      acceptUnverifiedStoreInvite
                      onChangeConnection={refreshExploreConnection}
                      connection={exploreConnection}
                      brandSlug={brandShowroom?.slug}
                      connectButtonVariant="contained"
                      shouldVerifyStorefront={false}
                      size="large"
                    />
                  </Box>
                )}
              </Box>
            </Grid>
            {isSharedLinkView && (
              <Grid>
                <ConnectionActionButton
                  connectButtonVariant="contained"
                  connection={{} as Explore}
                  brandSlug={brandShowroom.slug}
                  onChangeConnection={() => {
                    return;
                  }}
                />
              </Grid>
            )}
            {canShowEditButton && (
              <Grid>
                <EditButton viewMode={viewMode} onClick={toggleViewMode} />
              </Grid>
            )}
          </Grid>
        </Box>
      </div>
    </>
  );
};

export default BrandShowroom;
